// =====================================================
// Swiss Finance OS - Claude Sonnet 4.5 Classification
// Production-ready AI classification using Edge Function
// =====================================================

import { supabase } from '@/integrations/supabase/client';
import type { ParsedTransaction } from './swiss-banking-processors';

export interface ClassificationResult extends Record<string, unknown> {
  accountNumber: string;
  accountName: string;
  confidence: number;
  vatRate: number;
  vatApplicable: boolean;
  reasoning: string;
  riskFactors: string[];
  requiresReview: boolean;
  source: 'existing_rules' | 'claude_sonnet_4_5' | 'hybrid' | 'fallback' | 'deterministic';
  proposalId?: string;
}

interface ClassificationRequest {
  transaction: {
    description: string;
    amount: number;
    currency: string;
    type?: 'CRDT' | 'DBIT' | null;
    counterparty?: string | null;
    date: string;
  };
  mandateId?: string;
  organizationId?: string;
  mode?: 'production' | 'proposal_mode';
}

interface ClassificationResponse {
  classification: ClassificationResult;
  source: string;
  proposalId?: string;
  error?: string;
}

const CLASSIFICATION_TIMEOUT = 15000; // 15 seconds
const MAX_RETRIES = 2;

/**
 * Main classification function using Claude Sonnet 4.5 via Edge Function
 * Falls back to deterministic rules if Edge Function fails
 */
export async function classifyWithClaude(
  transaction: ParsedTransaction,
  mandateId?: string,
  organizationId?: string
): Promise<ClassificationResult> {
  // First try: Claude Edge Function
  try {
    const request: ClassificationRequest = {
      transaction: {
        description: transaction.description,
        amount: transaction.amount,
        currency: transaction.currency,
        type: transaction.amount < 0 ? 'DBIT' : 'CRDT',
        counterparty: transaction.counterparty || null,
        date: transaction.date,
      },
      mandateId,
      organizationId,
      mode: 'production',
    };

    const { data, error } = await supabase.functions.invoke<ClassificationResponse>(
      'ai-enhanced-classification',
      {
        body: request,
      }
    );

    if (error) {
      console.warn('Claude Edge Function error:', error);
      return fallbackClassification(transaction, 'edge_function_error');
    }

    if (!data || !data.classification) {
      console.warn('Claude Edge Function returned no classification');
      return fallbackClassification(transaction, 'no_classification');
    }

    // Log successful classification
    logClassification(transaction, data.classification, 'claude_edge_function');

    return {
      ...data.classification,
      source: data.classification.source || 'claude_sonnet_4_5',
    };

  } catch (error) {
    console.error('Claude classification error:', error);
    return fallbackClassification(transaction, 'exception');
  }
}

/**
 * Batch classification for multiple transactions
 * Optimized for processing large imports
 */
export async function classifyTransactionsBatch(
  transactions: ParsedTransaction[],
  mandateId?: string,
  organizationId?: string
): Promise<ClassificationResult[]> {
  const results: ClassificationResult[] = [];
  const batchSize = 5; // Process 5 at a time to avoid rate limits

  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);
    
    const batchResults = await Promise.all(
      batch.map(tx => classifyWithClaude(tx, mandateId, organizationId))
    );

    results.push(...batchResults);

    // Small delay to avoid rate limiting
    if (i + batchSize < transactions.length) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  return results;
}

/**
 * Fallback classification when Claude is unavailable
 * Returns a safe default requiring manual review
 */
function fallbackClassification(
  transaction: ParsedTransaction,
  reason: string
): ClassificationResult {
  const isDebit = transaction.amount < 0;

  // Simple heuristic fallback
  const account = isDebit ? '6800' : '3000'; // Diverse Aufwendungen / Produkteerlös
  const accountName = isDebit ? 'Diverse Aufwendungen' : 'Produkteerlös';

  return {
    accountNumber: account,
    accountName: accountName,
    confidence: 0,
    vatRate: 8.1,
    vatApplicable: false,
    reasoning: `Auto-classification unavailable (${reason}), manual review required`,
    riskFactors: ['low_confidence', 'fallback_classification'],
    requiresReview: true,
    source: 'fallback',
  };
}

/**
 * Log classification results for monitoring and debugging
 */
function logClassification(
  transaction: ParsedTransaction,
  result: ClassificationResult,
  method: string
): void {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event: 'classification',
    method,
    transaction: {
      date: transaction.date,
      amount: transaction.amount,
      counterparty: transaction.counterparty,
      source: transaction.source,
    },
    result: {
      account: result.accountNumber,
      confidence: result.confidence,
      source: result.source,
      requiresReview: result.requiresReview,
    },
  };

  console.log('[Classification]', JSON.stringify(logEntry));
}

/**
 * Store classification feedback for continuous learning
 */
export async function recordClassificationFeedback(
  transactionId: string,
  originalClassification: ClassificationResult,
  correctedAccount: string,
  correctedVatRate: number,
  userId: string,
  organizationId: string
): Promise<void> {
  try {
    const feedback = {
      transaction_id: transactionId,
      original_classification: originalClassification,
      human_correction: {
        accountNumber: correctedAccount,
        vatRate: correctedVatRate,
      },
      user_id: userId,
      organization_id: organizationId,
      created_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from('ai_classification_feedback')
      .insert([feedback as any]);

    if (error) {
      console.error('Failed to record feedback:', error);
    }
  } catch (error) {
    console.error('Error recording classification feedback:', error);
  }
}

export default {
  classifyWithClaude,
  classifyTransactionsBatch,
  recordClassificationFeedback,
};
